import { extractText } from "../chat/message-extract";
import type { GatewayBrowserClient } from "../gateway";
import { generateUUID } from "../uuid";
import type { ChatAttachment } from "../ui-types";
import { t } from "../i18n";

export type ChatState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  chatLoading: boolean;
  chatMessages: unknown[];
  chatThinkingLevel: string | null;
  chatSending: boolean;
  chatMessage: string;
  chatAttachments: ChatAttachment[];
  chatRunId: string | null;
  chatStream: string | null;
  chatStreamStartedAt: number | null;
  lastError: string | null;
};

export type ChatEventPayload = {
  runId: string;
  sessionKey: string;
  state: "delta" | "final" | "aborted" | "error";
  message?: unknown;
  errorMessage?: string;
};

const CHAT_HISTORY_TIMEOUT_MS = 15_000;

export async function loadChatHistory(state: ChatState) {
  if (!state.client || !state.connected) return;
  state.chatLoading = true;
  state.lastError = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    const request = state.client.request("chat.history", {
      sessionKey: state.sessionKey,
      limit: 200,
    }) as Promise<{ messages?: unknown[]; thinkingLevel?: string | null; memoryEnabled?: boolean }>;

    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`chat.history timeout after ${CHAT_HISTORY_TIMEOUT_MS}ms`));
      }, CHAT_HISTORY_TIMEOUT_MS);
    });

    const res = await Promise.race([request, timeout]);
    state.chatMessages = Array.isArray(res.messages) ? res.messages : [];
    state.chatThinkingLevel = res.thinkingLevel ?? null;
  } catch (err) {
    state.lastError = String(err);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    state.chatLoading = false;
  }
}

const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;
const MAX_ATTACHMENTS_TOTAL_BYTES = 12 * 1024 * 1024;

function dataUrlToBase64(dataUrl: string): { content: string; mimeType: string } | null {
  // Accept both explicit and omitted mime type in data URLs.
  // Example with mime: data:text/plain;base64,AAAA
  // Example without mime: data:;base64,AAAA
  const match = /^data:([^;]*)?;base64,(.+)$/i.exec(dataUrl);
  if (!match) return null;
  const mimeType = match[1] && match[1].trim() ? match[1] : "application/octet-stream";
  return { mimeType, content: match[2] };
}

export async function sendChatMessage(
  state: ChatState,
  message: string,
  attachments?: ChatAttachment[],
): Promise<string | null> {
  if (!state.client || !state.connected) return null;
  const msg = message.trim();
  const hasAttachments = attachments && attachments.length > 0;
  if (!msg && !hasAttachments) return null;

  const now = Date.now();

  // Build user message content blocks
  const contentBlocks: Array<{ type: string; text?: string; source?: unknown }> = [];
  if (msg) {
    contentBlocks.push({ type: "text", text: msg });
  }
  // Add attachment previews to the message for display
  if (hasAttachments) {
    for (const att of attachments) {
      const isImage = att.mimeType.startsWith("image/");
      const parsed = dataUrlToBase64(att.dataUrl);
      contentBlocks.push({
        type: isImage ? "image" : "document",
        source: {
          type: "base64",
          media_type: parsed?.mimeType ?? att.mimeType,
          data: parsed?.content ?? att.dataUrl,
        },
        ...(att.fileName ? { fileName: att.fileName } : {}),
      });
    }
  }

  state.chatMessages = [
    ...state.chatMessages,
    {
      role: "user",
      content: contentBlocks,
      timestamp: now,
    },
  ];

  state.chatSending = true;
  state.lastError = null;
  const runId = generateUUID();
  state.chatRunId = runId;
  state.chatStream = "";
  state.chatStreamStartedAt = now;

  // Convert attachments to API format
  const apiAttachments = hasAttachments
    ? attachments
        .map((att) => {
          const parsed = dataUrlToBase64(att.dataUrl);
          if (!parsed) return null;
          const isImage = att.mimeType.startsWith("image/");
          return {
            type: isImage ? "image" : "document",
            mimeType: parsed.mimeType,
            content: parsed.content,
            ...(att.fileName ? { fileName: att.fileName } : {}),
          };
        })
        .filter((a): a is NonNullable<typeof a> => a !== null)
    : undefined;

  if (apiAttachments?.length) {
    const sizes = apiAttachments.map((att) => Math.round((att.content.length * 3) / 4));
    const max = Math.max(...sizes);
    const total = sizes.reduce((sum, n) => sum + n, 0);
    if (max > MAX_ATTACHMENT_BYTES || total > MAX_ATTACHMENTS_TOTAL_BYTES) {
      const error =
        `Attachment too large (max ${(MAX_ATTACHMENT_BYTES / (1024 * 1024)).toFixed(0)}MB/file, ` +
        `${(MAX_ATTACHMENTS_TOTAL_BYTES / (1024 * 1024)).toFixed(0)}MB total).`;
      state.chatRunId = null;
      state.chatStream = null;
      state.chatStreamStartedAt = null;
      state.lastError = error;
      state.chatMessages = [
        ...state.chatMessages,
        {
          role: "assistant",
          content: [{ type: "text", text: t().controllers.error + error }],
          timestamp: Date.now(),
        },
      ];
      state.chatSending = false;
      return null;
    }
  }

  try {
    await state.client.request("chat.send", {
      sessionKey: state.sessionKey,
      message: msg,
      deliver: false,
      idempotencyKey: runId,
      attachments: apiAttachments,
    });
    return runId;
  } catch (err) {
    const error = String(err);
    state.chatRunId = null;
    state.chatStream = null;
    state.chatStreamStartedAt = null;
    state.lastError = error;
    state.chatMessages = [
      ...state.chatMessages,
      {
        role: "assistant",
        content: [{ type: "text", text: t().controllers.error + error }],
        timestamp: Date.now(),
      },
    ];
    return null;
  } finally {
    state.chatSending = false;
  }
}

export async function abortChatRun(state: ChatState): Promise<boolean> {
  if (!state.client || !state.connected) return false;
  const runId = state.chatRunId;
  try {
    await state.client.request(
      "chat.abort",
      runId ? { sessionKey: state.sessionKey, runId } : { sessionKey: state.sessionKey },
    );
    return true;
  } catch (err) {
    state.lastError = String(err);
    return false;
  }
}

export function handleChatEvent(state: ChatState, payload?: ChatEventPayload) {
  if (!payload) return null;
  if (payload.sessionKey !== state.sessionKey) return null;

  // Final from another run (e.g. sub-agent announce): refresh history to show new message.
  // See https://github.com/openclaw/openclaw/issues/1909
  if (payload.runId && state.chatRunId && payload.runId !== state.chatRunId) {
    if (payload.state === "final") return "final";
    return null;
  }

  if (payload.state === "delta") {
    const next = extractText(payload.message);
    if (typeof next === "string") {
      const current = state.chatStream ?? "";
      if (!current || next.length >= current.length) {
        state.chatStream = next;
      }
    }
  } else if (payload.state === "final") {
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
  } else if (payload.state === "aborted") {
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
  } else if (payload.state === "error") {
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
    state.lastError = payload.errorMessage ?? "chat error";
  }
  return payload.state;
}
